import React, { useState, useEffect, useRef } from 'react';
import { CustomerProfile, CustomerOrder } from '../../../types';
import { getSupabase, updateCustomerProfile, getCustomerOrdersRpc } from '../../../utils/supabase';
import { formatOrderDateTime, createWhatsAppLink } from '../../../utils/formatters';
import { resolvePersistedOrderServiceFee } from '../../../utils/deliveryFee';
import {
  User,
  LogOut,
  RefreshCw,
  Gift,
  Award,
  ArrowRight,
  ArrowLeft,
  X,
  Lock,
  ChevronRight,
  Info,
  Receipt,
  Bell,
  Store,
  HelpCircle,
  FileText,
  Search,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  TrendingUp,
  Copy,
  MapPin,
  Ticket,
  ShieldCheck,
  Shield,
} from 'lucide-react';
import {
  activateCustomerDeviceNotifications,
  isOneSignalSubscribed,
} from '../../../utils/oneSignal';
import {
  getCustomerLoyalty,
  getLoyaltyRewards,
  getRewardRedemptions,
  getLoyaltyTransactions,
  redeemReward,
  CustomerLoyaltyData,
  LoyaltyReward,
  PointTransaction,
  RewardRedemption,
} from '../../../utils/supabaseLoyalty';
import {
  getMembershipTierSettings,
  calculateMembershipTier,
  isOrderValidForTier,
  fetchCustomerValidOrdersCount,
  fetchCustomerSuccessfulReferralsCount,
  MembershipTierSettings,
  DEFAULT_MEMBERSHIP_TIER_SETTINGS,
  CalculatedCustomerTier,
} from '../../../utils/supabaseMembershipTier';
import {
  getReferralRewardSettings,
  getVerifiedCachedReferralSettings,
  ReferralRewardSettings,
} from '../../../utils/supabaseReferralSettings';
import { fetchCustomerReferralStats, CustomerReferralStats } from '../../../utils/supabaseReferral';
import { useContent } from '../../../context/ContentContext';
import { resolveMediaUrl } from '../../../utils/api';
import { formatRupiah } from '../../../utils/formatters';

interface CustomerProfileTabProps {
  profile: CustomerProfile;
  onLogout: () => void;
  onProfileUpdate: (updated: Partial<CustomerProfile>) => void;
  onStartOrder?: () => void;
  onBack?: () => void;
}

// Local coffee-shop character illustration avatars (stored locally in /public/avatars/ with zero external dependency)
const CUSTOMER_ILLUSTRATION_AVATARS = [
  '/avatars/coffee-avatar-1.svg',
  '/avatars/coffee-avatar-2.svg',
  '/avatars/coffee-avatar-3.svg',
  '/avatars/coffee-avatar-4.svg',
  '/avatars/coffee-avatar-5.svg',
  '/avatars/coffee-avatar-6.svg',
] as const;

function getCustomerIllustrationAvatar(seed?: string): string {
  const cleanSeed = (seed || 'leton-customer').trim();
  let hash = 0;
  for (let i = 0; i < cleanSeed.length; i++) {
    hash = (hash * 31 + cleanSeed.charCodeAt(i)) >>> 0;
  }
  return CUSTOMER_ILLUSTRATION_AVATARS[hash % CUSTOMER_ILLUSTRATION_AVATARS.length];
}

const DEFAULT_AVATAR_FALLBACK_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#1E3A5F"/>
        <stop offset="100%" stop-color="#0F1E36"/>
      </linearGradient>
      <linearGradient id="shirt" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#0054CD"/>
        <stop offset="100%" stop-color="#0B2E6B"/>
      </linearGradient>
    </defs>
    <rect width="160" height="160" rx="80" fill="url(#bg)"/>
    <circle cx="122" cy="34" r="24" fill="#FBBF24" opacity="0.12"/>
    <path d="M26 160 C28 118, 52 104, 80 104 C108 104, 132 118, 134 160 Z" fill="url(#shirt)"/>
    <path d="M64 104 L80 120 L96 104 Z" fill="#F8FAFC"/>
    <rect x="69" y="86" width="22" height="24" rx="10" fill="#E6B89C"/>
    <circle cx="51" cy="68" r="7" fill="#E6B89C"/>
    <circle cx="109" cy="68" r="7" fill="#E6B89C"/>
    <rect x="52" y="36" width="56" height="60" rx="28" fill="#F3C6AA"/>
    <path d="M48 62 C46 36, 64 24, 82 25 C102 26, 113 38, 111 60 C107 47, 96 42, 82 43 C66 44, 55 49, 48 62 Z" fill="#2A1B12"/>
    <path d="M63 58 Q68 55 73 58" stroke="#2A1B12" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <path d="M87 58 Q92 55 97 58" stroke="#2A1B12" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <circle cx="68" cy="65" r="3.2" fill="#1E293B"/>
    <circle cx="92" cy="65" r="3.2" fill="#1E293B"/>
    <circle cx="69" cy="64" r="1" fill="#FFFFFF"/>
    <circle cx="93" cy="64" r="1" fill="#FFFFFF"/>
    <path d="M72 79 Q80 86 88 79" stroke="#9A5B42" stroke-width="2.8" stroke-linecap="round" fill="none"/>
    <circle cx="62" cy="73" r="4.5" fill="#E07A5F" opacity="0.25"/>
    <circle cx="98" cy="73" r="4.5" fill="#E07A5F" opacity="0.25"/>
  </svg>`
)}`;

const TIER_CARD_ORDER: Array<'SILVER' | 'GOLD' | 'PLATINUM'> = ['SILVER', 'GOLD', 'PLATINUM'];

export default function CustomerProfileTab({
  profile,
  onLogout,
  onProfileUpdate,
  onStartOrder,
  onBack,
}: CustomerProfileTabProps) {
  const { data } = useContent();
  const letonLogoSrc = resolveMediaUrl(data?.siteSettings?.logoUrl) || '/logo_icon.jpg';

  const [orders, setOrders] = useState<(CustomerOrder & { order_items?: any[] })[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Loyalty & Rewards state
  const [loyaltyData, setLoyaltyData] = useState<CustomerLoyaltyData | null>(null);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [redemptions, setRedemptions] = useState<RewardRedemption[]>([]);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [redeemingRewardId, setRedeemingRewardId] = useState<string | null>(null);
  const [confirmingReward, setConfirmingReward] = useState<LoyaltyReward | null>(null);
  const [activeProof, setActiveProof] = useState<RewardRedemption | null>(null);

  // Membership Tier & Referral states
  const [tierSettings, setTierSettings] = useState<MembershipTierSettings>(DEFAULT_MEMBERSHIP_TIER_SETTINGS);
  const [referralSettings, setReferralSettings] = useState<ReferralRewardSettings | null>(() =>
    getVerifiedCachedReferralSettings()
  );
  const [loadingReferralSettings, setLoadingReferralSettings] = useState<boolean>(
    () => getVerifiedCachedReferralSettings() === null
  );
  const [tierData, setTierData] = useState<CalculatedCustomerTier | null>(null);
  const [successfulReferralsCount, setSuccessfulReferralsCount] = useState<number>(0);

  // Swipeable Membership Card state & refs
  const tierCarouselRef = useRef<HTMLDivElement | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const hasSyncedInitialTierSlideRef = useRef<string | null>(null);
  const isMouseDraggingCardRef = useRef<boolean>(false);
  const dragStartXRef = useRef<number>(0);
  const dragStartScrollLeftRef = useRef<number>(0);

  const [danaBound, setDanaBound] = useState<boolean>(false);
  const [danaBindingMask, setDanaBindingMask] = useState<string>("");
  const [loadingDana, setLoadingDana] = useState<boolean>(false);
  const [danaError, setDanaError] = useState<string>("");
  const [danaSuccessMessage, setDanaSuccessMessage] = useState<string>("");
  const isProcessingDanaCallbackRef = useRef<boolean>(false);

  // Fetch live binding status on mount
  useEffect(() => {
    const fetchDanaStatus = async () => {
      try {
        const token = localStorage.getItem("leton_customer_token");
        if (!token) return;
        const res = await fetch("/api/payment/dana/binding-status", {
          headers: { Authorization: "Bearer " + token }
        });
        const data = await res.json().catch(() => ({}));
        if (data && data.success) {
          setDanaBound(Boolean(data.isBound));
          if (data.bindingIdMasked) setDanaBindingMask(data.bindingIdMasked);
        }
      } catch (err) {
        console.warn("[DANA Status] Failed to fetch binding status");
      }
    };
    fetchDanaStatus();
  }, []);

  // Process return callback with authCode and state verification
  useEffect(() => {
    if (typeof window === "undefined") return;

    let authCode: string | null = null;
    let state: string | null = null;

    try {
      const url = new URL(window.location.href);
      authCode = url.searchParams.get("authCode");
      state = url.searchParams.get("state");

      if (!authCode && window.location.search) {
        const sp = new URLSearchParams(window.location.search);
        authCode = sp.get("authCode");
        state = sp.get("state");
      }

      if (!authCode && window.location.hash) {
        const hash = window.location.hash;
        const qIdx = hash.indexOf("?");
        if (qIdx !== -1) {
          const hashQuery = hash.substring(qIdx + 1);
          const params = new URLSearchParams(hashQuery);
          authCode = params.get("authCode");
          if (!state) state = params.get("state");
        }
      }
    } catch {}

    if (authCode && authCode.trim()) {
      if (isProcessingDanaCallbackRef.current) return;
      isProcessingDanaCallbackRef.current = true;

      const savedState = sessionStorage.getItem("dana_oauth_state");
      sessionStorage.removeItem("dana_oauth_state");

      if (!savedState || !state || savedState.trim() !== state.trim()) {
        setDanaError("Validasi keamanan penautan DANA gagal (CSRF mismatch/session missing). Silakan lakukan penautan ulang.");
        try {
          const cleanHash = window.location.hash ? window.location.hash.split("?")[0] : "";
          const cleanUrl = window.location.origin + window.location.pathname + cleanHash;
          window.history.replaceState({}, document.title, cleanUrl);
        } catch {}
        return;
      }

      try {
        const cleanHash = window.location.hash ? window.location.hash.split("?")[0] : "";
        const cleanUrl = window.location.origin + window.location.pathname + cleanHash;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch {}

      const executeTokenExchange = async () => {
        setLoadingDana(true);
        setDanaError("");
        setDanaSuccessMessage("");
        try {
          const token = localStorage.getItem("leton_customer_token");
          if (!token) {
            setDanaError("Sesi login Anda telah berakhir. Silakan masuk kembali.");
            setLoadingDana(false);
            return;
          }

          const res = await fetch("/api/payment/dana/bind", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token
            },
            body: JSON.stringify({ authCode: authCode!.trim(), state: (state || '').trim() })
          });

          const data = await res.json().catch(() => ({}));
          if (res.ok && data && data.success) {
            setDanaBound(true);
            if (data.bindingIdMasked) setDanaBindingMask(data.bindingIdMasked);
            setDanaSuccessMessage("Akun DANA Anda berhasil dihubungkan.");
          } else {
            setDanaError(data?.error || "Gagal menghubungkan akun DANA.");
          }
        } catch (err: any) {
          setDanaError("Gangguan koneksi saat menukar kode otorisasi DANA.");
        } finally {
          setLoadingDana(false);
        }
      };

      executeTokenExchange();
    }
  }, []);

  const handleConnectDana = async () => {
    setLoadingDana(true);
    setDanaError("");
    setDanaSuccessMessage("");

    try {
      const token = localStorage.getItem("leton_customer_token");
      if (!token) {
        setDanaError("Silakan masuk terlebih dahulu untuk menghubungkan akun DANA.");
        setLoadingDana(false);
        return;
      }

      const res = await fetch("/api/payment/dana/binding-url", {
        headers: { Authorization: "Bearer " + token }
      });
      const data = await res.json().catch(() => ({}));

      if (data && data.isBound) {
        setDanaBound(true);
        if (data.bindingIdMasked) setDanaBindingMask(data.bindingIdMasked);
        setLoadingDana(false);
        return;
      }

      if (
        res.ok &&
        data &&
        data.success &&
        typeof data.bindingUrl === "string" &&
        data.bindingUrl.startsWith("http")
      ) {
        if (data.state) {
          sessionStorage.setItem("dana_oauth_state", data.state);
        }
        window.location.href = data.bindingUrl;
      } else {
        setDanaError(data?.error || "Layanan penautan DANA belum aktif. Hubungi administrator.");
        setLoadingDana(false);
      }
    } catch (err: any) {
      setDanaError("Gagal menghubungi server untuk memulai penautan akun DANA.");
      setLoadingDana(false);
    }
  };

  // Modals
  const [showEditProfileModal, setShowEditProfileModal] = useState<boolean>(false);
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState<boolean>(false);
  const [showRewardsModal, setShowRewardsModal] = useState<boolean>(false);
  const [rewardsActiveTab, setRewardsActiveTab] = useState<'points' | 'catalog' | 'vouchers'>('points');
  const [showTierModal, setShowTierModal] = useState<boolean>(false);

  // Form Edit Profile
  const [namaLengkap, setNamaLengkap] = useState(profile?.namaLengkap || '');
  const [tanggalLahir, setTanggalLahir] = useState(profile?.tanggalLahir || '');
  const [alamat, setAlamat] = useState(profile?.alamat || '');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [referralStats, setReferralStats] = useState<CustomerReferralStats>({
    referralCode: profile?.referralCode || '',
    referredFriends: [],
    totalReferred: 0,
    successfulReferrals: 0,
    pendingReferrals: 0,
    totalPointsEarned: 0,
    rewardTransactionCount: 0,
    isFromTransactionLedger: true,
  });
  const [isNotifActive, setIsNotifActive] = useState(false);
  const [isActivatingNotif, setIsActivatingNotif] = useState(false);
  const [notifMessage, setNotifMessage] = useState<string | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  const loadReferralStats = async () => {
    const custId = profile?.id || profile?.userId;
    if (!custId) return;
    try {
      const stats = await fetchCustomerReferralStats(custId, profile?.referralCode);
      setReferralStats(stats);
      if (stats.referralCode && stats.referralCode !== profile?.referralCode) {
        onProfileUpdate({ referralCode: stats.referralCode });
      }
    } catch {}
  };

  useEffect(() => {
    loadReferralStats();
  }, [profile?.id, profile?.userId, profile?.referralCode]);

  const activeReferralCode = (referralStats.referralCode || profile?.referralCode || '').trim();

  const handleCopyReferral = () => {
    if (!activeReferralCode) return;
    navigator.clipboard.writeText(activeReferralCode).then(() => {
      setCopiedReferral(true);
      setNotifMessage(`Kode referral "${activeReferralCode}" berhasil disalin!`);
      setTimeout(() => setCopiedReferral(false), 2500);
    }).catch(() => {
      alert(`Kode referral: ${activeReferralCode}`);
    });
  };

  useEffect(() => {
    isOneSignalSubscribed()
      .then((sub) => {
        setIsNotifActive(sub);
      })
      .catch(() => {});
  }, []);

  const handleActivateNotification = async () => {
    if (isActivatingNotif) return;
    setIsActivatingNotif(true);
    setNotifMessage(null);
    try {
      const res = await activateCustomerDeviceNotifications();
      if (res.success) {
        setIsNotifActive(true);
        setNotifMessage('Notifikasi pesanan berhasil diaktifkan untuk perangkat ini.');
      } else {
        if (res.permission === 'denied' || res.error?.includes('ditolak')) {
          setNotifMessage('Izin notifikasi diblokir di pengaturan browser.');
        } else {
          setNotifMessage(res.error || 'Izin notifikasi belum diizinkan.');
        }
      }
    } catch (err: any) {
      setNotifMessage('Gagal mengaktifkan notifikasi.');
    } finally {
      setIsActivatingNotif(false);
    }
  };

  const loadLoyaltyData = async () => {
    const targetUserId = profile?.userId || profile?.id;
    const targetPhone = profile?.nomorHp;
    if (!targetUserId && !targetPhone) return;
    setLoadingLoyalty(true);
    try {
      const custLoyalty = await getCustomerLoyalty(targetUserId, targetPhone);
      setLoyaltyData(custLoyalty);

      const rewardsRes = await getLoyaltyRewards();
      setRewards(rewardsRes.rewards.filter((r) => r.isActive));

      const rdmList = await getRewardRedemptions(targetUserId);
      setRedemptions(rdmList);

      const txList = await getLoyaltyTransactions(targetUserId, targetPhone);
      setTransactions(txList);
    } catch (err) {
      console.error('Error loading loyalty data:', err);
    } finally {
      setLoadingLoyalty(false);
    }
  };

  const handleExecuteRedeem = async (reward: LoyaltyReward) => {
    const targetUserId = profile?.userId || profile?.id;
    if (!targetUserId || !loyaltyData) return;

    if (loyaltyData.pointsBalance < reward.pointsRequired) {
      alert('Poin Anda tidak mencukupi untuk ditukar dengan reward ini.');
      setConfirmingReward(null);
      return;
    }

    setRedeemingRewardId(reward.id);
    try {
      const res = await redeemReward(targetUserId, reward, profile);
      if (res.success && res.redemption) {
        setConfirmingReward(null);
        await loadLoyaltyData();
        setActiveProof(res.redemption);
      } else {
        alert(res.error || 'Terjadi kesalahan saat menukarkan poin.');
      }
    } catch (err: any) {
      alert(err?.message || 'Gagal menukarkan poin.');
    } finally {
      setRedeemingRewardId(null);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const custId = profile?.id || profile?.userId || '';
      const custPhone = profile?.nomorHp || '';

      const [rpcResult, verifiedOrdersResult, refCount] = await Promise.all([
        getCustomerOrdersRpc().catch(() => ({ success: false, orders: [] })),
        fetchCustomerValidOrdersCount(custId, custPhone).catch(() => ({
          count: 0,
          validOrders: [],
          allOrders: [],
        })),
        custId ? fetchCustomerSuccessfulReferralsCount(custId) : Promise.resolve(0),
      ]);

      let ordersData: any[] = [];
      if (rpcResult.success && Array.isArray(rpcResult.orders)) {
        ordersData = rpcResult.orders;
      }

      const verifiedById: Record<string, any> = {};
      if (Array.isArray(verifiedOrdersResult.allOrders)) {
        verifiedOrdersResult.allOrders.forEach((vo: any) => {
          if (vo && vo.id) {
            verifiedById[vo.id] = vo;
          }
        });
      }

      const mappedRpcOrders: (CustomerOrder & { order_items?: any[] })[] = ordersData.map((o: any) => {
        const verifiedMatch = o.id ? verifiedById[o.id] : undefined;
        const rpcItems = Array.isArray(o.items) ? o.items : [];
        const verifiedItems =
          verifiedMatch && Array.isArray(verifiedMatch.order_items) && verifiedMatch.order_items.length > 0
            ? verifiedMatch.order_items
            : Array.isArray(o.order_items) && o.order_items.length > 0
            ? o.order_items
            : rpcItems;

        return {
          id: o.id,
          orderNumber: o.order_number || o.orderNumber || 'LTN-????',
          outletId: o.outlet_id || o.outletId || '',
          outletName: o.outlet_name || o.outletName || '',
          customerName: o.customer_name || o.customerName || '',
          customerPhone: o.customer_phone || o.customerPhone || '',
          customerId: o.customer_id || o.customerId,
          userId: o.user_id || o.userId,
          orderType: o.order_type || o.orderType || 'DINE IN',
          tableNumber: o.table_number || o.tableNumber || '',
          items: rpcItems.length > 0 ? rpcItems : verifiedItems,
          order_items: verifiedItems,
          totalAmount: Number(o.total_amount || o.totalAmount || 0),
          paymentMethod: o.payment_method || o.paymentMethod || 'QRIS',
          paymentStatus: o.payment_status || o.paymentStatus || 'WAITING PAYMENT',
          paymentReceiptUrl: o.payment_receipt_url || o.paymentReceiptUrl,
          paymentReceiptPath: o.payment_receipt_path || o.paymentReceiptPath,
          paymentProofPath: o.payment_proof_path || o.paymentProofPath,
          rejectionReason: o.rejection_reason || o.rejectionReason,
          orderStatus: o.order_status || o.orderStatus || 'NEW',
          customerNote: o.customer_note || o.customerNote || '',
          createdAt: o.created_at || o.createdAt,
          updatedAt: o.updated_at || o.updatedAt,
        };
      });

      const seenIds = new Set(mappedRpcOrders.map((o) => o.id));
      const combinedOrders: (CustomerOrder & { order_items?: any[] })[] = [...mappedRpcOrders];
      if (Array.isArray(verifiedOrdersResult.allOrders)) {
        verifiedOrdersResult.allOrders.forEach((vo: any) => {
          if (vo && vo.id && !seenIds.has(vo.id)) {
            seenIds.add(vo.id);
            combinedOrders.push(vo);
          }
        });
      }

      setOrders(combinedOrders);
      setSuccessfulReferralsCount(refCount);
    } catch (err) {
      console.error('Exception loading orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadTierConfig = async () => {
    try {
      const [tierRes, refRes] = await Promise.all([
        getMembershipTierSettings(),
        getReferralRewardSettings(),
      ]);
      setTierSettings(tierRes.settings);
      if (refRes && refRes.isVerified && refRes.settings) {
        setReferralSettings(refRes.settings);
      }
    } catch (err) {
      console.warn('Error loading tier settings:', err);
    } finally {
      setLoadingReferralSettings(false);
    }
  };

  useEffect(() => {
    const validOrders = orders.filter(isOrderValidForTier);
    const validCount = validOrders.length;
    const validSpending = validOrders.reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);
    setTierData(calculateMembershipTier(validCount, validSpending, successfulReferralsCount, tierSettings));
  }, [orders, tierSettings, successfulReferralsCount]);

  useEffect(() => {
    loadTierConfig();

    const handleTierSettingsEvent = (e: Event) => {
      const customEvt = e as CustomEvent<MembershipTierSettings>;
      if (customEvt.detail) {
        setTierSettings(customEvt.detail);
      } else {
        loadTierConfig();
      }
    };

    const handleReferralSettingsEvent = (e: Event) => {
      const customEvt = e as CustomEvent<ReferralRewardSettings>;
      if (customEvt.detail) {
        setReferralSettings(customEvt.detail);
        setLoadingReferralSettings(false);
      } else {
        loadTierConfig();
      }
    };

    window.addEventListener('leton_tier_settings_updated', handleTierSettingsEvent);
    window.addEventListener('leton_referral_settings_updated', handleReferralSettingsEvent);

    const client = getSupabase();
    const settingsChannel = client
      .channel('customer_profile_settings_sync')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leton_content',
          filter: 'id=eq.membership_tier_settings',
        },
        () => {
          loadTierConfig();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leton_content',
          filter: 'id=eq.referral_settings',
        },
        () => {
          loadTierConfig();
        }
      )
      .subscribe();

    const ordersChannel = client
      .channel('customer_profile_orders_realtime_sync')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
        },
        () => {
          loadOrders();
          loadReferralStats();
          loadLoyaltyData();
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener('leton_tier_settings_updated', handleTierSettingsEvent);
      window.removeEventListener('leton_referral_settings_updated', handleReferralSettingsEvent);
      try {
        client.removeChannel(settingsChannel);
      } catch {}
      try {
        client.removeChannel(ordersChannel);
      } catch {}
    };
  }, []);

  useEffect(() => {
    loadOrders();
    loadLoyaltyData();
  }, [profile?.userId, profile?.id]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!namaLengkap.trim()) {
      setErrorMsg('Nama Lengkap wajib diisi.');
      return;
    }

    setSavingProfile(true);
    try {
      const res = await updateCustomerProfile(namaLengkap, tanggalLahir, alamat);
      if (res.success) {
        setSuccessMsg('Profil berhasil diperbarui!');
        onProfileUpdate({
          namaLengkap: namaLengkap.trim(),
          tanggalLahir: tanggalLahir,
          alamat: alamat.trim(),
        });
        setTimeout(() => setShowEditProfileModal(false), 1000);
      } else {
        setErrorMsg(res.error || 'Gagal memperbarui profil.');
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Terjadi kesalahan sistem.');
    } finally {
      setSavingProfile(false);
    }
  };

  const filteredOrders = orders.filter((o) => {
    const num = (o.orderNumber || o.id || '').toLowerCase();
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return num.includes(query);
  });

  const whatsappNumber = data?.contactSettings?.whatsapp || '082168936647';
  const supportWaLink = createWhatsAppLink(
    whatsappNumber,
    `Halo LetonKu, saya ${profile?.namaLengkap || 'Customer'} (HP: ${profile?.nomorHp || '-'}), ingin menanyakan seputar akun & pesanan.`
  );

  const activeVouchersCount = redemptions.filter((r) => r.status === 'ACTIVE').length;
  const userPoints = loyaltyData ? loyaltyData.pointsBalance : (profile?.pointsBalance || 0);

  // Consistent per-customer coffee-shop character illustration avatar (always used locally for profile display)
  const customerSeed = profile?.id || profile?.userId || profile?.nomorHp || profile?.namaLengkap || 'leton-customer';
  const defaultCharacterAvatar = getCustomerIllustrationAvatar(customerSeed);
  const avatarImageSrc = defaultCharacterAvatar;

  // Deduplicated referrer reward transactions from loyalty_transactions & loyalty_registry
  const verifiedReferralTxSummary = (() => {
    const seenIds = new Set<string>();
    const seenOrderRefs = new Set<string>();
    let totalPoints = 0;
    let count = 0;

    for (const tx of transactions) {
      if (!tx || tx.transactionType !== 'EARN') continue;
      const reasonLower = String(tx.reason || '').toLowerCase();
      if (!reasonLower.includes('referral reward') || reasonLower.includes('referred member')) {
        continue;
      }
      const pts = Number(tx.points || 0);
      if (pts <= 0) continue;

      if (tx.id && seenIds.has(tx.id)) continue;
      const orderRef = tx.referenceOrderId ? String(tx.referenceOrderId).trim() : '';
      if (orderRef && orderRef !== 'FIRST-ORDER' && seenOrderRefs.has(orderRef)) {
        continue;
      }

      if (tx.id) seenIds.add(tx.id);
      if (orderRef && orderRef !== 'FIRST-ORDER') seenOrderRefs.add(orderRef);

      totalPoints += pts;
      count += 1;
    }

    return { totalPoints, count };
  })();

  const actualSuccessfulReferrals = Math.max(
    referralStats.successfulReferrals,
    verifiedReferralTxSummary.count,
    successfulReferralsCount
  );

  const actualReferralRewardPoints =
    verifiedReferralTxSummary.count > 0
      ? verifiedReferralTxSummary.totalPoints
      : referralStats.totalPointsEarned;

  const actualPendingReferrals = referralStats.pendingReferrals || 0;

  // Authentic Leton Membership Tier evaluation (SILVER, GOLD, PLATINUM)
  const currentTier = tierData?.tier || 'SILVER';
  const currentTierIndex = TIER_CARD_ORDER.indexOf(currentTier);
  const memberIdDisplay = `LTN-${(profile?.id || profile?.userId || '00000000').slice(0, 8).toUpperCase()}`;
  const memberNameDisplay = profile?.namaLengkap || 'PELANGGAN LETON';

  const scrollToTierSlide = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const track = tierCarouselRef.current;
    if (!track) return;
    const clampedIdx = Math.max(0, Math.min(TIER_CARD_ORDER.length - 1, index));
    const slideEl = track.children[clampedIdx] as HTMLElement | undefined;
    if (slideEl) {
      track.scrollTo({ left: slideEl.offsetLeft, behavior });
    } else {
      track.scrollTo({ left: clampedIdx * track.clientWidth, behavior });
    }
    setActiveSlideIndex(clampedIdx);
  };

  // Sync initial / updated active tier card position automatically
  useEffect(() => {
    const targetIdx = TIER_CARD_ORDER.indexOf(currentTier);
    if (targetIdx < 0) return;
    if (hasSyncedInitialTierSlideRef.current !== currentTier) {
      hasSyncedInitialTierSlideRef.current = currentTier;
      setActiveSlideIndex(targetIdx);
      const timer = window.setTimeout(() => {
        scrollToTierSlide(targetIdx, 'auto');
      }, 40);
      return () => window.clearTimeout(timer);
    }
  }, [currentTier]);

  const handleTierCarouselScroll = () => {
    const track = tierCarouselRef.current;
    if (!track || track.clientWidth === 0) return;
    const idx = Math.round(track.scrollLeft / track.clientWidth);
    const clamped = Math.max(0, Math.min(TIER_CARD_ORDER.length - 1, idx));
    if (clamped !== activeSlideIndex) {
      setActiveSlideIndex(clamped);
    }
  };

  const handleTierCarouselMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const track = tierCarouselRef.current;
    if (!track) return;
    isMouseDraggingCardRef.current = true;
    dragStartXRef.current = e.pageX - track.offsetLeft;
    dragStartScrollLeftRef.current = track.scrollLeft;
  };

  const handleTierCarouselMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMouseDraggingCardRef.current) return;
    const track = tierCarouselRef.current;
    if (!track) return;
    e.preventDefault();
    const x = e.pageX - track.offsetLeft;
    const walk = (x - dragStartXRef.current) * 1.15;
    track.scrollLeft = dragStartScrollLeftRef.current - walk;
  };

  const handleTierCarouselMouseEnd = () => {
    if (!isMouseDraggingCardRef.current) return;
    isMouseDraggingCardRef.current = false;
    const track = tierCarouselRef.current;
    if (!track || track.clientWidth === 0) return;
    const nearestIdx = Math.round(track.scrollLeft / track.clientWidth);
    scrollToTierSlide(nearestIdx, 'smooth');
  };

  const validTxCount = tierData?.transactionCount ?? 0;
  const validSpendingTotal = orders
    .filter(isOrderValidForTier)
    .reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);

  // Progress calculation helper for each swipeable card
  const silverToGoldProgressPercent = (() => {
    if (currentTier === 'GOLD' || currentTier === 'PLATINUM') return 100;
    return Math.min(100, Math.max(5, tierData ? tierData.progressPercent : 5));
  })();

  const silverRemainingLabel = (() => {
    if (currentTier === 'GOLD' || currentTier === 'PLATINUM') return 'Gold Tier Tercapai';
    const remTx = Math.max(0, tierSettings.goldMinTransactions - validTxCount);
    const remSpend = Math.max(0, tierSettings.goldMinSpending - validSpendingTotal);
    if (remTx > 0 && remSpend > 0) return `${remTx} Tx & ${formatRupiah(remSpend)} lagi`;
    if (remTx > 0) return `${remTx} Transaksi lagi`;
    if (remSpend > 0) return `${formatRupiah(remSpend)} lagi`;
    return 'Syarat Gold Terpenuhi';
  })();

  const goldToPlatinumProgressPercent = (() => {
    if (currentTier === 'PLATINUM') return 100;
    const txTarget = Math.max(1, tierSettings.platinumMinTransactions);
    const txRatio = Math.min(1, validTxCount / txTarget);
    const spendRatio =
      tierSettings.platinumMinSpending > 0
        ? Math.min(1, validSpendingTotal / tierSettings.platinumMinSpending)
        : 1;
    const combined =
      tierSettings.platinumMinSpending > 0 ? (txRatio + spendRatio) / 2 : txRatio;
    return Math.min(100, Math.max(5, Math.round(combined * 100)));
  })();

  const goldRemainingLabel = (() => {
    if (currentTier === 'PLATINUM') return 'Platinum Tier Tercapai';
    const remTx = Math.max(0, tierSettings.platinumMinTransactions - validTxCount);
    const remSpend = Math.max(0, tierSettings.platinumMinSpending - validSpendingTotal);
    if (remTx > 0 && remSpend > 0) return `${remTx} Tx & ${formatRupiah(remSpend)} lagi`;
    if (remTx > 0) return `${remTx} Transaksi lagi`;
    if (remSpend > 0) return `${formatRupiah(remSpend)} lagi`;
    return 'Syarat Platinum Terpenuhi';
  })();

  // Strict AND evaluation for each tier unlock status (Both Tx Count AND Accumulated Spending must be satisfied)
  const isSilverUnlocked =
    (validTxCount >= (tierSettings.silverMinTransactions || 0) &&
      validSpendingTotal >= (tierSettings.silverMinSpending || 0)) ||
    currentTier === 'SILVER' ||
    currentTier === 'GOLD' ||
    currentTier === 'PLATINUM';

  const isGoldUnlocked =
    (validTxCount >= (tierSettings.goldMinTransactions ?? 100) &&
      validSpendingTotal >= (tierSettings.goldMinSpending || 0)) ||
    currentTier === 'GOLD' ||
    currentTier === 'PLATINUM';

  const isPlatinumUnlocked =
    (validTxCount >= (tierSettings.platinumMinTransactions ?? 250) &&
      validSpendingTotal >= (tierSettings.platinumMinSpending || 0)) ||
    currentTier === 'PLATINUM';

  const isTierUnlocked = (tier: string) => {
    if (tier === 'SILVER') return isSilverUnlocked;
    if (tier === 'GOLD') return isGoldUnlocked;
    if (tier === 'PLATINUM') return isPlatinumUnlocked;
    return false;
  };

  const goldUnlockRemainingLabel = (() => {
    if (isGoldUnlocked) return 'Gold Terbuka';
    const remTx = Math.max(0, (tierSettings.goldMinTransactions ?? 100) - validTxCount);
    const remSpend = Math.max(0, (tierSettings.goldMinSpending || 0) - validSpendingTotal);
    if (remTx > 0 && remSpend > 0) return `${remTx} Tx & ${formatRupiah(remSpend)} lagi`;
    if (remTx > 0) return `${remTx} Tx lagi`;
    if (remSpend > 0) return `${formatRupiah(remSpend)} lagi`;
    return 'Syarat Gold Terpenuhi';
  })();

  const goldUnlockProgressPercent = (() => {
    if (isGoldUnlocked) return 100;
    const txTarget = Math.max(1, tierSettings.goldMinTransactions ?? 100);
    const txProgress = Math.min(100, Math.max(0, Math.round((validTxCount / txTarget) * 100)));
    const spendProgress =
      tierSettings.goldMinSpending > 0
        ? Math.min(100, Math.max(0, Math.round((validSpendingTotal / tierSettings.goldMinSpending) * 100)))
        : 100;
    return Math.min(txProgress, spendProgress);
  })();

  const platinumUnlockRemainingLabel = (() => {
    if (isPlatinumUnlocked) return 'Platinum Terbuka';
    const remTx = Math.max(0, (tierSettings.platinumMinTransactions ?? 250) - validTxCount);
    const remSpend = Math.max(0, (tierSettings.platinumMinSpending || 0) - validSpendingTotal);
    if (remTx > 0 && remSpend > 0) return `${remTx} Tx & ${formatRupiah(remSpend)} lagi`;
    if (remTx > 0) return `${remTx} Tx lagi`;
    if (remSpend > 0) return `${formatRupiah(remSpend)} lagi`;
    return 'Syarat Platinum Terpenuhi';
  })();

  const platinumUnlockProgressPercent = (() => {
    if (isPlatinumUnlocked) return 100;
    const txTarget = Math.max(1, tierSettings.platinumMinTransactions ?? 250);
    const txProgress = Math.min(100, Math.max(0, Math.round((validTxCount / txTarget) * 100)));
    const spendProgress =
      tierSettings.platinumMinSpending > 0
        ? Math.min(100, Math.max(0, Math.round((validSpendingTotal / tierSettings.platinumMinSpending) * 100)))
        : 100;
    return Math.min(txProgress, spendProgress);
  })();

  return (
    <div className="bg-[#F7F9FC] text-[#191C1E] font-sans antialiased min-h-screen pb-28 lg:pb-12 selection:bg-[#0054CD]/20">
      {/* Compact Top Bar Header */}
      <header className="bg-white/95 backdrop-blur-xl border-b border-slate-200/80 sticky top-0 z-30 flex items-center justify-between px-4 md:px-8 h-12 shadow-[0_1px_6px_rgba(0,0,0,0.03)] lg:hidden">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Kembali"
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[#191C1E] hover:bg-slate-200 transition-colors cursor-pointer shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <img
            src={letonLogoSrc}
            alt="LetonKu Logo"
            className="h-6 w-auto object-contain shrink-0 rounded-full"
            referrerPolicy="no-referrer"
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo_icon.jpg'; }}
          />
          <h1 className="text-sm sm:text-base font-bold text-[#191C1E] truncate">Profil Saya</h1>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={handleActivateNotification}
            aria-label="Notifikasi"
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer relative"
            title="Notifikasi"
          >
            <Bell className="w-4 h-4" />
            {isNotifActive && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowEditProfileModal(true)}
            aria-label="Pengaturan"
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Pengaturan Akun"
          >
            <User className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto flex flex-col relative w-full lg:py-6">
        {/* 1. HEADER PROFIL (COMPACT VERTICAL SPACING FOR SEAMLESS CARD OVERLAP) */}
        <section className="relative w-full bg-gradient-to-b from-[#0B192C] via-[#0E1E34] to-[#112239] text-white pt-3.5 pb-16 px-4 overflow-hidden lg:rounded-3xl">
          {/* Ambient Coffee SVG Shapes */}
          <div className="absolute -right-10 -top-8 w-52 h-52 opacity-15 pointer-events-none">
            <svg className="w-full h-full text-amber-300" fill="none" viewBox="0 0 200 200">
              <path d="M40,160 C30,90 90,40 160,30 C150,110 110,160 40,160 Z" stroke="currentColor" strokeDasharray="4 4" strokeWidth="2.5" />
              <path d="M40,160 C80,120 120,80 160,30" stroke="currentColor" strokeWidth="2" />
              <circle cx="160" cy="30" fill="currentColor" r="6" />
            </svg>
          </div>
          <div className="absolute -left-8 bottom-6 w-40 h-40 opacity-10 pointer-events-none">
            <svg className="w-full h-full text-blue-300" fill="none" viewBox="0 0 100 100">
              <path d="M20,80 C30,60 10,40 25,20 C35,40 25,60 40,80" stroke="currentColor" strokeWidth="2" />
              <path d="M45,75 C55,55 35,35 50,15 C60,35 50,55 65,75" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>

          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Avatar */}
            <div className="relative mb-1.5">
              <div className="p-0.5 rounded-full bg-gradient-to-tr from-[#0054CD] via-blue-400 to-amber-300 shadow-lg">
                <img
                  src={avatarImageSrc}
                  alt={profile?.namaLengkap || 'Pelanggan Leton'}
                  className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full object-cover object-center bg-slate-800"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const imgEl = e.currentTarget as HTMLImageElement;
                    if (!imgEl.src.endsWith(defaultCharacterAvatar)) {
                      imgEl.src = defaultCharacterAvatar;
                    } else {
                      imgEl.src = DEFAULT_AVATAR_FALLBACK_SVG;
                    }
                  }}
                />
              </div>
            </div>

            {/* Name & Verified Icon */}
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <h2 className="text-lg sm:text-xl font-bold tracking-tight text-white leading-snug">
                {profile?.namaLengkap || 'Pelanggan Leton'}
              </h2>
              <ShieldCheck className="w-4 h-4 text-amber-300 fill-amber-400/20 shrink-0" />
            </div>

            {/* Tier Chip Badge */}
            <div
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border shadow-2xs mb-1.5 ${
                currentTier === 'PLATINUM'
                  ? 'bg-cyan-400/20 border-cyan-300/40 text-cyan-200'
                  : currentTier === 'GOLD'
                  ? 'bg-amber-400/20 border-amber-300/40 text-amber-200'
                  : 'bg-slate-200/20 border-slate-300/35 text-slate-100'
              }`}
            >
              <Award className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase font-bold tracking-wider">
                Leton {currentTier} Member
              </span>
            </div>

            <p className="text-[11px] text-slate-300 max-w-[270px] leading-snug">
              Kumpulkan Leton Poin dan tukar dengan reward serta racikan kopi istimewa setiap hari.
            </p>
          </div>
        </section>

        {/* 2. KARTU MEMBERSHIP (LETON PRIVILEGE) — SWIPEABLE HORIZONTAL CAROUSEL ACROSS SILVER, GOLD, PLATINUM */}
        <div className="px-4 -mt-11 relative z-20">
          <div
            ref={tierCarouselRef}
            onScroll={handleTierCarouselScroll}
            onMouseDown={handleTierCarouselMouseDown}
            onMouseMove={handleTierCarouselMouseMove}
            onMouseUp={handleTierCarouselMouseEnd}
            onMouseLeave={handleTierCarouselMouseEnd}
            style={{ touchAction: 'pan-x pan-y', WebkitOverflowScrolling: 'touch' }}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth select-none cursor-grab active:cursor-grabbing pb-1"
          >
            {/* SLIDE 1: SILVER TIER CARD — Metallic Palladium Silver, Slate Grey & Deep Blue */}
            <div className="w-full shrink-0 snap-center">
              <div className="rounded-2xl p-[1px] bg-gradient-to-b from-slate-200 via-slate-300 to-slate-400 shadow-[0_14px_30px_-8px_rgba(30,41,59,0.38)]">
                <section
                  className="relative w-full rounded-[15px] overflow-hidden text-white p-3.5 sm:p-4"
                  style={{ background: 'linear-gradient(135deg, #2C425C 0%, #4E6E8E 48%, #1C2D42 100%)' }}
                >
                  {/* Frosted Silver Specular Sheen */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/25 blur-2xl rounded-full pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-70 pointer-events-none" />

                  <div className="relative z-10 flex flex-col justify-between">
                    {/* Top Bar */}
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-md border border-white/35 flex items-center justify-center shadow-inner shrink-0 overflow-hidden p-1">
                          <img
                            src={letonLogoSrc}
                            alt="LetonKu"
                            className="w-full h-full object-contain rounded-lg"
                            referrerPolicy="no-referrer"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo_icon.jpg'; }}
                          />
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-[0.18em] text-slate-200 font-semibold leading-none mb-0.5">
                            LETON POINT MEMBER
                          </p>
                          <div className="flex items-center gap-1.5">
                            <h3 className="text-xs sm:text-sm font-extrabold text-white leading-tight tracking-tight">
                              SILVER TIER
                            </h3>
                            {!isSilverUnlocked ? (
                              <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-900/60 border border-slate-500/50 text-slate-200">
                                <Lock className="w-2.5 h-2.5 text-slate-300 shrink-0" /> Terkunci
                              </span>
                            ) : (
                              <span
                                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${
                                  currentTier === 'SILVER'
                                    ? 'bg-emerald-400/25 border-emerald-300/50 text-emerald-100'
                                    : 'bg-white/15 border-white/25 text-slate-100'
                                }`}
                              >
                                {currentTier === 'SILVER' ? 'Tier Aktif' : 'Tercapai'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTierModal(true)}
                        className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-[9px] font-bold tracking-wider uppercase border border-white/35 transition-colors cursor-pointer"
                      >
                        <span>🥈 SILVER</span>
                      </button>
                    </div>

                    {/* Member Info */}
                    <div className="mb-2.5 flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[8px] uppercase text-slate-200/80 tracking-wider font-semibold">Nama Anggota</p>
                        <p className="text-xs sm:text-sm font-bold tracking-wide uppercase text-white truncate">
                          {memberNameDisplay}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[8px] uppercase text-slate-200/80 tracking-wider font-semibold">ID Anggota</p>
                        <p className="text-[11px] text-slate-100 tracking-widest font-mono font-semibold">
                          {memberIdDisplay}
                        </p>
                      </div>
                    </div>

                    {/* Progress toward Gold Tier */}
                    <div className="bg-slate-950/35 rounded-xl p-2.5 backdrop-blur-xs mb-2 border border-white/20">
                      <div className="flex justify-between items-center mb-1 text-[10px]">
                        <span className="text-slate-100 font-medium">Menuju Gold Tier</span>
                        <span className="text-amber-300 font-bold">{silverRemainingLabel}</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-white/20 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-slate-200 via-sky-200 to-amber-300 transition-all duration-500"
                          style={{ width: `${silverToGoldProgressPercent}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center mt-1 text-[9px] text-slate-200/90 font-medium">
                        <span>Transaksi Anda: {validTxCount} Tx</span>
                        <span>
                          Target Gold: {tierSettings.goldMinTransactions} Tx
                          {tierSettings.goldMinSpending > 0 ? ` • ${formatRupiah(tierSettings.goldMinSpending)}` : ''}
                        </span>
                      </div>
                    </div>

                    {/* Active Silver Benefits */}
                    <div className="flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-white text-[9px] backdrop-blur-md border border-white/20">
                        <CheckCircle className="w-2.5 h-2.5 text-slate-100 shrink-0" /> 1x Poin Setiap Transaksi
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-white text-[9px] backdrop-blur-md border border-white/20">
                        <CheckCircle className="w-2.5 h-2.5 text-slate-100 shrink-0" /> Akses Voucher &amp; Promo Member
                      </span>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* SLIDE 2: GOLD TIER CARD — Signature Reserve Rich Navy & Metallic Gold */}
            <div className="w-full shrink-0 snap-center">
              <div className="rounded-2xl p-[1px] bg-gradient-to-b from-[#D4AF37] via-[#F5E6BE]/70 to-[#91751D] shadow-[0_16px_34px_-8px_rgba(212,175,55,0.35)]">
                <section
                  className="relative w-full rounded-[15px] overflow-hidden text-white p-3.5 sm:p-4"
                  style={{ background: 'linear-gradient(135deg, #0B132B 0%, #152449 60%, #070D1E 100%)' }}
                >
                  {/* Warm Champagne & Gold Glow */}
                  <div className="absolute -top-12 -right-12 w-44 h-44 bg-amber-400/20 blur-2xl rounded-full pointer-events-none" />
                  <div className="absolute inset-0 bg-[radial-gradient(#D4AF37_1px,transparent_1px)] [background-size:16px_16px] opacity-15 pointer-events-none" />

                  {/* Blurred & Dimmed Card Content when locked (~40% blur intensity) */}
                  <div
                    className={`relative z-10 flex flex-col justify-between transition-all duration-300 ${
                      !isGoldUnlocked ? 'filter blur-[1.2px] opacity-45 select-none pointer-events-none' : ''
                    }`}
                  >
                    {/* Top Bar */}
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-amber-400/20 border border-[#D4AF37]/50 backdrop-blur-md flex items-center justify-center shadow-inner shrink-0 overflow-hidden p-1">
                          <img
                            src={letonLogoSrc}
                            alt="LetonKu"
                            className="w-full h-full object-contain rounded-lg"
                            referrerPolicy="no-referrer"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo_icon.jpg'; }}
                          />
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold leading-none mb-0.5">
                            LETON POINT MEMBER
                          </p>
                          <div className="flex items-center gap-2">
                            <h3 className="text-xs sm:text-sm font-extrabold text-[#F5E6BE] leading-tight tracking-tight">
                              GOLD TIER
                            </h3>
                            {isGoldUnlocked && (
                              <span
                                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${
                                  currentTier === 'GOLD'
                                    ? 'bg-emerald-400/25 border-emerald-300/50 text-emerald-100'
                                    : 'bg-amber-400/20 border-[#D4AF37]/40 text-[#F5E6BE]'
                                }`}
                              >
                                {currentTier === 'GOLD' ? 'Tier Aktif' : 'Tercapai'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTierModal(true)}
                        className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-400/20 hover:bg-amber-400/30 text-[#F5E6BE] text-[9px] font-bold tracking-wider uppercase border border-[#D4AF37]/50 transition-colors cursor-pointer"
                      >
                        <span>🥇 GOLD</span>
                      </button>
                    </div>

                    {/* Member Info */}
                    <div className="mb-2.5 flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[8px] uppercase text-[#D4AF37]/80 tracking-wider font-semibold">Nama Anggota</p>
                        <p className="text-xs sm:text-sm font-bold tracking-wide uppercase text-white truncate">
                          {memberNameDisplay}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[8px] uppercase text-[#D4AF37]/80 tracking-wider font-semibold">ID Anggota</p>
                        <p className="text-[11px] text-[#F5E6BE] tracking-widest font-mono font-semibold">
                          {memberIdDisplay}
                        </p>
                      </div>
                    </div>

                    {/* Progress / Syarat Tier Card */}
                    <div className="bg-black/40 rounded-xl p-2.5 backdrop-blur-xs mb-2 border border-[#D4AF37]/30">
                      <div className="flex justify-between items-center mb-1 text-[10px]">
                        <span className="text-[#F5E6BE] font-medium">
                          {!isGoldUnlocked ? 'Syarat Buka Gold Tier' : 'Menuju Platinum Tier'}
                        </span>
                        <span className="text-amber-300 font-bold">
                          {!isGoldUnlocked ? goldUnlockRemainingLabel : goldRemainingLabel}
                        </span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-white/15 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] via-amber-300 to-cyan-300 transition-all duration-500"
                          style={{ width: `${!isGoldUnlocked ? goldUnlockProgressPercent : goldToPlatinumProgressPercent}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center mt-1 text-[9px] text-slate-300 font-medium">
                        <span>Transaksi: {validTxCount} / {!isGoldUnlocked ? tierSettings.goldMinTransactions : tierSettings.platinumMinTransactions} Tx</span>
                        <span>
                          Belanja: {formatRupiah(validSpendingTotal)}
                          {(!isGoldUnlocked ? tierSettings.goldMinSpending : tierSettings.platinumMinSpending) > 0
                            ? ` / ${formatRupiah(!isGoldUnlocked ? tierSettings.goldMinSpending : tierSettings.platinumMinSpending)}`
                            : ''}
                        </span>
                      </div>
                    </div>

                    {/* Active Gold Benefits */}
                    <div className="flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-[#F5E6BE] text-[9px] backdrop-blur-md border border-[#D4AF37]/35">
                        <CheckCircle className="w-2.5 h-2.5 text-[#D4AF37] shrink-0" /> Level 2 Signature Reserve
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-[#F5E6BE] text-[9px] backdrop-blur-md border border-[#D4AF37]/35">
                        <CheckCircle className="w-2.5 h-2.5 text-[#D4AF37] shrink-0" /> Keistimewaan &amp; Voucher Gold
                      </span>
                    </div>
                  </div>

                  {/* Elegant Dark Transparent Overlay with Gold Tint when Locked */}
                  {!isGoldUnlocked && (
                    <div
                      onClick={() => setShowTierModal(true)}
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center p-4 bg-gradient-to-b from-[#0B132B]/75 via-[#132042]/80 to-[#070D1E]/85 transition-all cursor-pointer rounded-[15px] select-none"
                    >
                      <div className="w-13 h-13 rounded-2xl bg-amber-400/20 border border-[#D4AF37]/60 flex items-center justify-center shadow-[0_8px_24px_rgba(212,175,55,0.3)] mb-2 group-hover:scale-105 transition-transform">
                        <Lock className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <span className="text-xs sm:text-sm font-extrabold tracking-[0.22em] text-[#F5E6BE] uppercase drop-shadow-md">
                        TIER TERKUNCI
                      </span>
                      <p className="text-[10px] text-amber-200/90 font-medium mt-1 text-center max-w-[240px]">
                        {goldUnlockRemainingLabel}
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </div>

            {/* SLIDE 3: PLATINUM TIER CARD — Pearl Platinum-Silver & Royal Sapphire */}
            <div className="w-full shrink-0 snap-center">
              <div className="rounded-2xl p-[1.5px] bg-gradient-to-r from-slate-300 via-cyan-200 to-[#0F2C59] shadow-[0_16px_36px_-8px_rgba(15,44,89,0.28)]">
                <section
                  className="relative w-full rounded-[14.5px] overflow-hidden text-[#0F172A] p-3.5 sm:p-4"
                  style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 50%, #E2E8F0 100%)' }}
                >
                  {/* Iridescent Sapphire & Platinum Glow */}
                  <div className="absolute -top-12 -right-12 w-44 h-44 bg-sky-200/60 blur-2xl rounded-full pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-transparent to-slate-200/50 pointer-events-none" />

                  {/* Blurred & Dimmed Card Content when locked (~40% blur intensity) */}
                  <div
                    className={`relative z-10 flex flex-col justify-between transition-all duration-300 ${
                      !isPlatinumUnlocked ? 'filter blur-[1.2px] opacity-45 select-none pointer-events-none' : ''
                    }`}
                  >
                    {/* Top Bar */}
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-[#0F2C59]/10 border border-[#0F2C59]/25 flex items-center justify-center shadow-inner shrink-0 overflow-hidden p-1">
                          <img
                            src={letonLogoSrc}
                            alt="LetonKu"
                            className="w-full h-full object-contain rounded-lg"
                            referrerPolicy="no-referrer"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo_icon.jpg'; }}
                          />
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-[0.18em] text-[#0F2C59]/80 font-bold leading-none mb-0.5">
                            LETON POINT MEMBER
                          </p>
                          <div className="flex items-center gap-2">
                            <h3 className="text-xs sm:text-sm font-extrabold text-[#0F2C59] leading-tight tracking-tight">
                              PLATINUM TIER
                            </h3>
                            {isPlatinumUnlocked && (
                              <span
                                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full border bg-emerald-600 text-white border-emerald-700`}
                              >
                                Tier Aktif
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTierModal(true)}
                        className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#0F2C59] hover:bg-[#163A75] text-white text-[9px] font-bold tracking-wider uppercase shadow-2xs transition-colors cursor-pointer"
                      >
                        <span>💎 PLATINUM VIP</span>
                      </button>
                    </div>

                    {/* Member Info */}
                    <div className="mb-2.5 flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[8px] uppercase text-slate-500 tracking-wider font-semibold">Nama Anggota</p>
                        <p className="text-xs sm:text-sm font-extrabold tracking-wide uppercase text-[#0F172A] truncate">
                          {memberNameDisplay}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[8px] uppercase text-slate-500 tracking-wider font-semibold">ID Anggota</p>
                        <p className="text-[11px] text-[#0F2C59] tracking-widest font-mono font-bold">
                          {memberIdDisplay}
                        </p>
                      </div>
                    </div>

                    {/* Platinum Tier Status & Progress */}
                    <div className="bg-[#0F2C59]/8 rounded-xl p-2.5 mb-2 border border-[#0F2C59]/20">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] font-bold text-[#0F2C59] truncate">
                          {isPlatinumUnlocked
                            ? 'Status Kasta Tertinggi • VIP Elite Atelier Aktif'
                            : 'Syarat Buka Platinum Tier'}
                        </span>
                        <span className="text-[10px] font-mono font-bold text-[#0F2C59] bg-white/80 px-2 py-0.5 rounded-md border border-[#0F2C59]/15 shrink-0">
                          {isPlatinumUnlocked ? `${validTxCount} Tx Valid` : platinumUnlockRemainingLabel}
                        </span>
                      </div>
                      {!isPlatinumUnlocked && (
                        <>
                          <div className="w-full h-1.5 rounded-full bg-slate-200/80 overflow-hidden my-1">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-[#0F2C59] transition-all duration-500"
                              style={{ width: `${platinumUnlockProgressPercent}%` }}
                            />
                          </div>
                          <div className="flex justify-between items-center text-[9px] text-slate-600 font-medium">
                            <span>Transaksi: {validTxCount} / {tierSettings.platinumMinTransactions} Tx</span>
                            <span>
                              Belanja: {formatRupiah(validSpendingTotal)}
                              {tierSettings.platinumMinSpending > 0
                                ? ` / ${formatRupiah(tierSettings.platinumMinSpending)}`
                                : ''}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Active Platinum Benefits */}
                    <div className="flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0F2C59]/10 text-[#0F2C59] text-[9px] font-semibold border border-[#0F2C59]/20">
                        <CheckCircle className="w-2.5 h-2.5 text-[#0F2C59] shrink-0" /> Kasta VIP Tertinggi Leton
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0F2C59]/10 text-[#0F2C59] text-[9px] font-semibold border border-[#0F2C59]/20">
                        <CheckCircle className="w-2.5 h-2.5 text-[#0F2C59] shrink-0" /> Keistimewaan &amp; Reward Platinum
                      </span>
                    </div>
                  </div>

                  {/* Elegant Dark Transparent Overlay with Sapphire Tint when Locked */}
                  {!isPlatinumUnlocked && (
                    <div
                      onClick={() => setShowTierModal(true)}
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center p-4 bg-gradient-to-b from-[#091527]/75 via-[#0E203B]/80 to-[#06101E]/85 transition-all cursor-pointer rounded-[14.5px] select-none"
                    >
                      <div className="w-13 h-13 rounded-2xl bg-cyan-500/20 border border-sky-400/50 flex items-center justify-center shadow-[0_8px_24px_rgba(56,189,248,0.3)] mb-2 group-hover:scale-105 transition-transform">
                        <Lock className="w-7 h-7 text-sky-300" />
                      </div>
                      <span className="text-xs sm:text-sm font-extrabold tracking-[0.22em] text-cyan-100 uppercase drop-shadow-md">
                        TIER TERKUNCI
                      </span>
                      <p className="text-[10px] text-sky-200/90 font-medium mt-1 text-center max-w-[240px]">
                        {platinumUnlockRemainingLabel}
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>

          {/* Interactive Swipe Indicator Dots & Tier Switcher Pills */}
          <div className="flex items-center justify-center gap-1.5 mt-1.5">
            {TIER_CARD_ORDER.map((tierKey, idx) => {
              const isSelected = activeSlideIndex === idx;
              const isUserActualTier = currentTierIndex === idx;
              return (
                <button
                  key={tierKey}
                  type="button"
                  onClick={() => scrollToTierSlide(idx, 'smooth')}
                  aria-label={`Lihat kartu ${tierKey}`}
                  className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold transition-all cursor-pointer border ${
                    isSelected
                      ? 'bg-[#0B192C] text-white border-[#0B192C] shadow-2xs'
                      : 'bg-white/90 text-slate-500 border-slate-200/80 hover:text-slate-800'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isUserActualTier
                        ? 'bg-emerald-400'
                        : isSelected
                        ? 'bg-amber-300'
                        : 'bg-slate-300'
                    }`}
                  />
                  <span>{tierKey}</span>
                  {!isTierUnlocked(tierKey) && (
                    <Lock className={`w-2.5 h-2.5 ${isSelected ? 'text-white/70' : 'text-slate-400'}`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. KARTU RINGKASAN LETON POINTS DAN VOUCHER AKTIF */}
        <div className="px-4 mt-2.5 relative z-10">
          <div className="w-full bg-white rounded-2xl shadow-2xs p-3.5 flex items-stretch divide-x divide-slate-100 border border-slate-200/80">
            {/* Left: Leton Points */}
            <div className="flex-1 pr-3 flex flex-col justify-between">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-800">
                  <Gift className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-600">Leton Points</span>
              </div>
              <div className="my-0.5">
                <span className="text-2xl font-extrabold text-slate-900 font-mono">
                  {loadingLoyalty ? '...' : userPoints.toLocaleString('id-ID')}
                </span>
                <span className="text-xs text-slate-500 ml-1 font-medium">pts</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-slate-400">Saldo Aktif</span>
                <button
                  type="button"
                  onClick={() => {
                    setRewardsActiveTab('catalog');
                    setShowRewardsModal(true);
                  }}
                  className="text-xs text-[#0054CD] font-bold flex items-center gap-0.5 hover:underline cursor-pointer"
                >
                  Tukar <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Right: Voucher Saya */}
            <div className="flex-1 pl-3 flex flex-col justify-between">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[#0054CD]">
                  <Ticket className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-600">Voucher Aktif</span>
              </div>
              <div className="my-0.5">
                <span className="text-2xl font-extrabold text-[#0054CD] font-mono">
                  {activeVouchersCount}
                </span>
                <span className="text-xs text-slate-500 ml-1 font-medium">Kupon</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-slate-400">Siap Pakai</span>
                <button
                  type="button"
                  onClick={() => {
                    setRewardsActiveTab('vouchers');
                    setShowRewardsModal(true);
                  }}
                  className="text-xs text-[#0054CD] font-bold flex items-center gap-0.5 hover:underline cursor-pointer"
                >
                  Semua <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 4. AKSI CEPAT */}
        <section className="px-4 mt-4">
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-base font-bold text-slate-900">Aksi Cepat</h3>
            <span className="text-xs text-[#0054CD] font-bold">Pintasan</span>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {/* Lihat Badge */}
            <button
              type="button"
              onClick={() => setShowTierModal(true)}
              className="flex flex-col items-center text-center p-3 rounded-2xl bg-white border border-slate-200/80 shadow-2xs hover:shadow-md transition-all active:scale-95 group cursor-pointer"
            >
              <div className="w-11 h-11 rounded-2xl bg-blue-50 text-[#0054CD] flex items-center justify-center mb-1 group-hover:scale-105 transition-transform border border-blue-100">
                <Award className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold text-slate-900 leading-tight">Lihat Badge</span>
            </button>

            {/* Dapatkan Poin */}
            <button
              type="button"
              onClick={() => {
                if (onStartOrder) {
                  onStartOrder();
                } else {
                  setRewardsActiveTab('points');
                  setShowRewardsModal(true);
                }
              }}
              className="flex flex-col items-center text-center p-3 rounded-2xl bg-white border border-slate-200/80 shadow-2xs hover:shadow-md transition-all active:scale-95 group cursor-pointer"
            >
              <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-1 group-hover:scale-105 transition-transform border border-amber-100">
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold text-slate-900 leading-tight">Dapatkan Poin</span>
            </button>

            {/* Tukar Reward */}
            <button
              type="button"
              onClick={() => {
                setRewardsActiveTab('catalog');
                setShowRewardsModal(true);
              }}
              className="flex flex-col items-center text-center p-3 rounded-2xl bg-white border border-slate-200/80 shadow-2xs hover:shadow-md transition-all active:scale-95 group cursor-pointer"
            >
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-1 group-hover:scale-105 transition-transform border border-emerald-100">
                <Gift className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold text-slate-900 leading-tight">Tukar Reward</span>
            </button>
          </div>
        </section>

        {/* 5. MEMBER GET MEMBER — CONNECTED TO ADMIN PUSAT & ACTUAL TRANSACTION LEDGER */}
        <section className="px-4 mt-4">
          <div className="rounded-2xl bg-gradient-to-br from-blue-50/80 via-white to-amber-50/50 p-4 border border-blue-100 shadow-2xs relative overflow-hidden space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#0054CD] text-white flex items-center justify-center shrink-0 shadow-md">
                <Gift className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-slate-900 leading-snug">Ajak Teman, Dapatkan Poin!</h4>
                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                  {referralSettings ? (
                    <>
                      Bagikan kode referral kamu dan dapatkan{' '}
                      <strong className="text-[#0054CD] font-bold">
                        {referralSettings.referrer_reward.toLocaleString('id-ID')} Poin
                      </strong>{' '}
                      untuk setiap teman yang menyelesaikan pesanan pertama
                      {referralSettings.referred_reward > 0 ? (
                        <>
                          {' '}(temanmu juga mendapat bonus{' '}
                          <strong className="text-[#0054CD] font-bold">
                            {referralSettings.referred_reward.toLocaleString('id-ID')} Poin
                          </strong>
                          )
                        </>
                      ) : null}
                      .
                    </>
                  ) : loadingReferralSettings ? (
                    'Memuat konfigurasi poin reward referral dari Admin Pusat...'
                  ) : (
                    'Bagikan kode referral kamu dan dapatkan bonus Leton Poin untuk setiap teman yang menyelesaikan pesanan pertama.'
                  )}
                </p>
              </div>
            </div>

            {/* Stats Bar — Actual Referred Members & Transaction-Verified Reward Points */}
            <div className="grid grid-cols-2 gap-2 bg-white/90 p-2.5 rounded-xl border border-slate-100">
              <div className="text-center">
                <span className="text-[11px] text-slate-500 block">Anggota Diundang</span>
                <span className="text-xs font-bold text-slate-900">
                  {actualSuccessfulReferrals} Teman
                </span>
                {actualPendingReferrals > 0 && (
                  <span className="text-[10px] text-amber-600 block mt-0.5 font-medium">
                    +{actualPendingReferrals} menunggu pesanan pertama
                  </span>
                )}
              </div>
              <div className="text-center border-l border-slate-100">
                <span className="text-[11px] text-slate-500 block">Total Reward</span>
                <span className="text-xs font-bold text-[#0054CD]">
                  {actualReferralRewardPoints.toLocaleString('id-ID')} Pts
                </span>
              </div>
            </div>

            {/* Referral Code Box */}
            <div className="flex items-center gap-2 bg-white rounded-xl p-2 pl-3 border border-slate-200 shadow-2xs">
              <div className="flex-1 flex flex-col">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Kode Unik Kamu</span>
                <span className="text-base font-extrabold tracking-wider text-[#0054CD] font-mono">
                  {activeReferralCode || 'Memuat...'}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopyReferral}
                disabled={!activeReferralCode}
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 active:scale-95 disabled:opacity-50 text-slate-800 text-xs font-bold flex items-center gap-1 transition-all cursor-pointer"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{copiedReferral ? 'Disalin!' : 'Salin'}</span>
              </button>
            </div>

            {/* Primary CTA Button */}
            <button
              type="button"
              disabled={!activeReferralCode}
              onClick={() => {
                if (!activeReferralCode) return;
                const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://letoncoffee.com';
                const friendBonusText =
                  referralSettings && referralSettings.referred_reward > 0
                    ? ` dan dapatkan bonus ${referralSettings.referred_reward.toLocaleString('id-ID')} Poin pada pesanan pertamamu`
                    : '';
                const text = `Hai! Yuk ngopi di LetonKu. Pakai kode referral saya "${activeReferralCode}"${friendBonusText} atau buka link: ${baseUrl}/?ref=${activeReferralCode}`;
                if (navigator.share) {
                  navigator.share({ title: 'LetonKu Referral', text }).catch(() => {});
                } else {
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                }
              }}
              className="w-full h-11 rounded-full bg-[#0054CD] hover:bg-blue-700 active:scale-98 disabled:opacity-50 text-white text-xs sm:text-sm font-bold flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer"
            >
              <span>Ajak Teman Pakai Leton</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* 6. MENU PENGATURAN AKUN DAN DANA */}
        <section className="px-4 mt-4 mb-6 space-y-4">
          {/* Notification status message toast if triggered */}
          {notifMessage && (
            <div className="p-3 rounded-2xl bg-blue-50 border border-blue-200 text-[#0054CD] text-xs flex items-center justify-between gap-2 shadow-2xs">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 shrink-0" />
                <span>{notifMessage}</span>
              </div>
              <button
                type="button"
                onClick={() => setNotifMessage(null)}
                className="p-1 text-[#0054CD] hover:bg-blue-100 rounded-lg cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* DANA Account Binding Section */}
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#118EEA] text-white font-black text-xs flex items-center justify-center shadow-2xs">
                  DANA
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-900">Akun DANA</h3>
                  <p className="text-[11px] text-slate-500">Tautkan akun DANA untuk checkout instan</p>
                </div>
              </div>
              {danaBound && (
                <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-600" /> Terhubung
                </span>
              )}
            </div>

            {danaSuccessMessage && (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{danaSuccessMessage}</span>
              </div>
            )}

            {danaError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                <span>{danaError}</span>
              </div>
            )}

            {danaBound ? (
              <div className="p-3 bg-sky-50 border border-sky-100 rounded-xl flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] font-bold text-sky-800 uppercase tracking-wider block">ID Penautan</span>
                  <span className="font-mono font-bold text-slate-800">{danaBindingMask || "Aktif"}</span>
                </div>
                <span className="text-[11px] text-slate-500">SNAP BI Ready</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleConnectDana}
                disabled={loadingDana}
                className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                  loadingDana
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-[#118EEA] hover:bg-sky-600 text-white cursor-pointer active:scale-[0.99] shadow-xs"
                }`}
              >
                {loadingDana ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Menghubungkan ke DANA...</span>
                  </>
                ) : (
                  <span>Hubungkan Akun DANA</span>
                )}
              </button>
            )}
          </div>

          <div>
            <h3 className="text-base font-bold text-slate-900 mb-2.5">Pengaturan Akun</h3>
            <div className="bg-white rounded-2xl shadow-2xs border border-slate-200 overflow-hidden flex flex-col divide-y divide-slate-100">
              {/* Edit Profil */}
              <button
                type="button"
                onClick={() => setShowEditProfileModal(true)}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-slate-50 transition-colors group text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:text-[#0054CD] group-hover:bg-blue-50 transition-colors">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Edit Profil</p>
                    <p className="text-[11px] text-slate-500">Perbarui nama, tanggal lahir & alamat</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0054CD] group-hover:translate-x-0.5 transition-all" />
              </button>

              {/* Alamat Saya */}
              <button
                type="button"
                onClick={() => setShowEditProfileModal(true)}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-slate-50 transition-colors group text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:text-[#0054CD] group-hover:bg-blue-50 transition-colors">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Alamat Saya</p>
                    <p className="text-[11px] text-slate-500">Kelola alamat pengiriman favorit</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0054CD] group-hover:translate-x-0.5 transition-all" />
              </button>

              {/* Riwayat Pesanan */}
              <button
                type="button"
                onClick={() => setShowOrderHistoryModal(true)}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-slate-50 transition-colors group text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:text-[#0054CD] group-hover:bg-blue-50 transition-colors">
                    <Receipt className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Riwayat Pesanan</p>
                    <p className="text-[11px] text-slate-500">Semua struk dan riwayat transaksi ({orders.length})</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0054CD] group-hover:translate-x-0.5 transition-all" />
              </button>

              {/* Bantuan & Layanan */}
              <button
                type="button"
                onClick={() => window.open(supportWaLink, '_blank')}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-slate-50 transition-colors group text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:text-[#0054CD] group-hover:bg-blue-50 transition-colors">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Bantuan &amp; Layanan</p>
                    <p className="text-[11px] text-slate-500">Hubungi Barista Care 24/7</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0054CD] group-hover:translate-x-0.5 transition-all" />
              </button>

              {/* Kebijakan Privasi */}
              <button
                type="button"
                onClick={() => {
                  try {
                    window.history.pushState(null, '', '/privacy-policy');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  } catch {
                    window.location.href = '/privacy-policy';
                  }
                }}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-slate-50 transition-colors group text-left w-full cursor-pointer border-t border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-[#0054CD] group-hover:scale-105 transition-transform">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900">Kebijakan Privasi</p>
                    <p className="text-[11px] text-slate-500">Perlindungan data &amp; privasi Anda</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#0054CD] group-hover:translate-x-0.5 transition-all" />
              </button>

              {/* Keluar Akun */}
              <button
                type="button"
                onClick={onLogout}
                className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-rose-50/50 transition-colors group text-left w-full cursor-pointer border-t border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 group-hover:scale-105 transition-transform">
                    <LogOut className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-bold text-rose-600">Keluar Akun</p>
                    <p className="text-[11px] text-slate-500">Akhiri sesi di perangkat ini</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-500" />
              </button>
            </div>
          </div>

          {/* Footer Note */}
          <div className="text-center py-2 space-y-1">
            <p className="text-[11px] text-slate-400">
              LetonKu App v2.4.1 • Terhubung dengan Supabase
            </p>
            <div>
              <button
                type="button"
                onClick={() => {
                  try {
                    window.history.pushState(null, '', '/privacy-policy');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  } catch {
                    window.location.href = '/privacy-policy';
                  }
                }}
                className="text-[11px] text-[#0054CD] hover:underline font-medium cursor-pointer"
              >
                Kebijakan Privasi &amp; Penghapusan Akun
              </button>
            </div>
            <p className="text-[10px] text-slate-400/80">
              Designed for Specialty Coffee Lovers in Dumai
            </p>
          </div>
        </section>
      </main>

      {/* MODAL 1: EDIT PROFILE */}
      {showEditProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-5 bg-[#0F172A] text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#0054CD]" />
                <h3 className="font-bold text-base">Edit Informasi Profil</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowEditProfileModal(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveProfile} className="p-6 space-y-4">
              {errorMsg && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              {successMsg && (
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  <span>{successMsg}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nama Lengkap</label>
                <input
                  type="text"
                  value={namaLengkap}
                  onChange={(e) => setNamaLengkap(e.target.value)}
                  placeholder="Masukkan nama Anda"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-[#0054CD] focus:ring-2 focus:ring-[#0054CD]/20 outline-none text-sm transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Tanggal Lahir</label>
                <input
                  type="date"
                  value={tanggalLahir}
                  onChange={(e) => setTanggalLahir(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-[#0054CD] focus:ring-2 focus:ring-[#0054CD]/20 outline-none text-sm transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nomor HP / WhatsApp</label>
                <input
                  type="text"
                  value={profile?.nomorHp || ''}
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-100 bg-slate-50 text-slate-500 font-mono text-sm cursor-not-allowed"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">Nomor HP digunakan sebagai ID login terverifikasi.</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Domisili (Opsional)</label>
                <textarea
                  value={alamat}
                  onChange={(e) => setAlamat(e.target.value)}
                  rows={2}
                  placeholder="Masukkan alamat domisili Anda"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-[#0054CD] focus:ring-2 focus:ring-[#0054CD]/20 outline-none text-sm transition-all resize-none"
                />
              </div>

              <div className="flex gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditProfileModal(false)}
                  className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="flex-1 py-2.5 px-4 bg-[#0054CD] hover:bg-blue-700 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  {savingProfile ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan Perubahan</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ORDER HISTORY */}
      {showOrderHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-lg h-[80vh] flex flex-col overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-5 bg-[#0F172A] text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#0054CD]" />
                <h3 className="font-bold text-base">Riwayat Pesanan Anda</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowOrderHistoryModal(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 bg-slate-50 border-b border-slate-100 shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cari nomor pesanan..."
                  className="w-full pl-10 pr-4 py-2 bg-white rounded-xl border border-slate-200 text-xs focus:border-[#0054CD] outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingOrders ? (
                <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
                  <RefreshCw className="w-6 h-6 animate-spin text-[#0054CD]" />
                  <span>Memuat pesanan...</span>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="py-12 text-center text-slate-400 space-y-2">
                  <Receipt className="w-10 h-10 mx-auto text-slate-300" />
                  <p className="text-xs">Belum ada riwayat pesanan.</p>
                </div>
              ) : (
                filteredOrders.map((o) => {
                  const isExpanded = expandedOrderId === o.id;
                  return (
                    <div
                      key={o.id}
                      className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-all shadow-2xs space-y-2.5"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-slate-900">{o.orderNumber || o.id}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            o.orderStatus === 'COMPLETED'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : o.orderStatus === 'CANCELLED' || o.orderStatus === 'REJECTED'
                              ? 'bg-rose-50 text-rose-700 border border-rose-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {o.orderStatus}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{o.outletName || 'Outlet LetonKu'}</span>
                        <span className="font-mono">{formatOrderDateTime(o.createdAt)}</span>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                        <div>
                          <span className="text-slate-500 block text-[10px]">Total Transaksi</span>
                          <span className="font-bold text-[#0054CD] font-mono">{formatRupiah(o.totalAmount)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId(isExpanded ? null : o.id)}
                          className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 text-xs transition cursor-pointer"
                        >
                          {isExpanded ? 'Sembunyikan' : 'Rincian'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="pt-2 border-t border-slate-100 text-xs space-y-1.5 bg-slate-50/70 p-3 rounded-xl mt-2">
                          <p className="font-bold text-slate-700 text-[11px] uppercase">Rincian Item:</p>
                          {(o.items || o.order_items || []).map((item: any, idx: number) => (
                            <div key={idx} className="flex justify-between text-slate-600 text-xs">
                              <span>
                                {item.quantity || 1}x {item.name || item.product_name || 'Kopi Leton'}
                              </span>
                              <span className="font-mono">
                                {formatRupiah((item.unitPrice || item.price || item.unit_price || 0) * (item.quantity || 1))}
                              </span>
                            </div>
                          ))}
                          <div className="pt-1.5 mt-1.5 border-t border-slate-200/80 space-y-1">
                            <div className="flex justify-between text-[11px] text-slate-500">
                              <span>Subtotal Pesanan</span>
                              <span className="font-mono">
                                {formatRupiah(
                                  (o.items || o.order_items || []).reduce(
                                    (acc: number, it: any) => acc + ((it.unitPrice || it.price || it.unit_price || 0) * (it.quantity || 1)),
                                    0
                                  )
                                )}
                              </span>
                            </div>
                            {Number(o.discountAmount || o.discount_amount || 0) > 0 && (
                              <div className="flex justify-between text-[11px] text-emerald-600">
                                <span>Diskon Voucher</span>
                                <span className="font-mono">-{formatRupiah(Number(o.discountAmount || o.discount_amount || 0))}</span>
                              </div>
                            )}
                            {o.orderType === 'DELIVERY' && Number(o.deliveryFee ?? o.delivery_fee ?? 0) > 0 && (
                              <div className="flex justify-between text-[11px] text-slate-500">
                                <span>Ongkos Kirim</span>
                                <span className="font-mono">{formatRupiah(Number(o.deliveryFee ?? o.delivery_fee ?? 0))}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-[11px] text-slate-500">
                              <span>Biaya Layanan</span>
                              <span className="font-mono">{formatRupiah(resolvePersistedOrderServiceFee(o))}</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold text-slate-900 pt-1 border-t border-slate-200/80">
                              <span>Total Pembayaran</span>
                              <span className="font-mono text-[#0054CD]">{formatRupiah(o.totalAmount)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: LOYALTY & REWARDS */}
      {showRewardsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-md h-[82vh] flex flex-col overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-5 bg-[#0F172A] text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-base">Leton Rewards & Vouchers</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowRewardsModal(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex border-b border-slate-200 bg-slate-50 p-1 shrink-0">
              <button
                type="button"
                onClick={() => setRewardsActiveTab('points')}
                className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  rewardsActiveTab === 'points'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Saldo Poin ({userPoints})
              </button>
              <button
                type="button"
                onClick={() => setRewardsActiveTab('catalog')}
                className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  rewardsActiveTab === 'catalog'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Katalog ({rewards.length})
              </button>
              <button
                type="button"
                onClick={() => setRewardsActiveTab('vouchers')}
                className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  rewardsActiveTab === 'vouchers'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Voucher Saya ({activeVouchersCount})
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {rewardsActiveTab === 'points' && (
                <div className="space-y-4">
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-md text-center">
                    <span className="text-xs uppercase font-bold tracking-wider opacity-90 block">Saldo Poin Aktif Anda</span>
                    <span className="text-4xl font-extrabold font-mono mt-1 block">
                      {userPoints.toLocaleString('id-ID')}
                    </span>
                    <span className="text-xs bg-white/20 px-3 py-1 rounded-full inline-block mt-2 font-medium">
                      Tukarkan poin dengan voucher diskon &amp; produk gratis
                    </span>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Riwayat Transaksi Poin</h4>
                    {transactions.length === 0 ? (
                      <p className="text-xs text-slate-400 py-4 text-center">Belum ada riwayat transaksi poin.</p>
                    ) : (
                      transactions.map((tx) => (
                        <div key={tx.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                          <div>
                            <p className="font-bold text-slate-800">{tx.reason || tx.transactionType}</p>
                            <span className="text-[10px] text-slate-400">{formatOrderDateTime(tx.createdAt)}</span>
                          </div>
                          <span className={`font-mono font-bold ${tx.points >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {tx.points >= 0 ? `+${tx.points}` : tx.points}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {rewardsActiveTab === 'catalog' && (
                <div className="space-y-3">
                  {rewards.length === 0 ? (
                    <p className="text-xs text-slate-400 py-8 text-center">Belum ada reward aktif di katalog.</p>
                  ) : (
                    rewards.map((r) => {
                      const canRedeem = userPoints >= r.pointsRequired;
                      return (
                        <div key={r.id} className="p-4 rounded-2xl border border-slate-200 bg-white shadow-2xs flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-bold text-slate-900 truncate">{r.name}</h4>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</p>
                            <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                              <Gift className="w-3.5 h-3.5" />
                              <span>{r.pointsRequired} Pts</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setConfirmingReward(r)}
                            disabled={!canRedeem}
                            className={`px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 transition-all ${
                              canRedeem
                                ? 'bg-[#0054CD] hover:bg-blue-700 text-white shadow-xs cursor-pointer'
                                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                          >
                            Tukar
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {rewardsActiveTab === 'vouchers' && (
                <div className="space-y-3">
                  {redemptions.length === 0 ? (
                    <p className="text-xs text-slate-400 py-8 text-center">Belum ada voucher yang ditukarkan.</p>
                  ) : (
                    redemptions.map((rdm) => (
                      <div key={rdm.id} className="p-4 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-slate-900">{rdm.rewardName}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            rdm.status === 'ACTIVE'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {rdm.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                          <span className="font-mono text-slate-600 font-bold">{rdm.voucherCode}</span>
                          <button
                            type="button"
                            onClick={() => setActiveProof(rdm)}
                            className="px-3 py-1 rounded-lg bg-blue-50 text-[#0054CD] font-bold hover:bg-blue-100 text-xs transition cursor-pointer"
                          >
                            Tampilkan QR
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM REDEEM REWARD MODAL */}
      {confirmingReward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 space-y-4 text-center shadow-2xl">
            <Gift className="w-12 h-12 text-amber-500 mx-auto" />
            <h3 className="text-base font-bold text-slate-900">Konfirmasi Penukaran Poin</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Anda akan menukarkan <strong className="text-amber-600 font-bold">{confirmingReward.pointsRequired} Poin</strong> untuk reward:
              <br />
              <span className="font-bold text-slate-900 text-sm mt-1 block">{confirmingReward.name}</span>
            </p>
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setConfirmingReward(null)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => handleExecuteRedeem(confirmingReward)}
                disabled={Boolean(redeemingRewardId)}
                className="flex-1 py-2.5 bg-[#0054CD] hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-xs transition cursor-pointer"
              >
                {redeemingRewardId ? 'Proses...' : 'Tukarkan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVE PROOF / QR PROOF MODAL */}
      {activeProof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 space-y-4 text-center shadow-2xl relative">
            <button
              type="button"
              onClick={() => setActiveProof(null)}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
            <Ticket className="w-10 h-10 text-[#0054CD] mx-auto" />
            <h3 className="text-base font-bold text-slate-900">{activeProof.rewardName}</h3>
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 font-mono text-center">
              <span className="text-xs text-slate-400 block mb-1">Kode Voucher</span>
              <span className="text-xl font-extrabold text-[#0054CD] tracking-widest block">{activeProof.voucherCode}</span>
            </div>
            <p className="text-[11px] text-slate-500">Tunjukkan kode ini kepada kasir saat memesan atau gunakan saat checkout online.</p>
          </div>
        </div>
      )}

      {/* MODAL 4: TIER BENEFITS DETAIL */}
      {showTierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-5 bg-[#0F172A] text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold text-base">Manfaat Membership Tier</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowTierModal(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <div className={`p-4 rounded-2xl border space-y-2 ${currentTier === 'SILVER' ? 'bg-slate-100 border-slate-400 ring-1 ring-slate-300' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between font-bold text-slate-900">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🥈</span>
                    <span>Silver Tier</span>
                  </div>
                  {currentTier === 'SILVER' && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-800 text-white text-[10px] uppercase">Tier Anda</span>
                  )}
                </div>
                <p className="text-slate-600">Syarat: {tierSettings.silverMinTransactions} Transaksi Valid.</p>
                <ul className="list-disc list-inside text-slate-500 space-y-1 pl-1">
                  <li>Dapatkan 1x Poin per transaksi</li>
                  <li>Akses voucher promo member</li>
                </ul>
              </div>

              <div className={`p-4 rounded-2xl border space-y-2 ${currentTier === 'GOLD' ? 'bg-amber-50 border-amber-400 ring-1 ring-amber-300' : 'bg-amber-50/70 border-amber-200'}`}>
                <div className="flex items-center justify-between font-bold text-amber-900">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🥇</span>
                    <span>Gold Tier</span>
                  </div>
                  {currentTier === 'GOLD' && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-600 text-white text-[10px] uppercase">Tier Anda</span>
                  )}
                </div>
                <p className="text-amber-800">
                  Syarat: Minimal {tierSettings.goldMinTransactions} Transaksi Valid atau belanja {formatRupiah(tierSettings.goldMinSpending)}.
                </p>
                <ul className="list-disc list-inside text-amber-700 space-y-1 pl-1">
                  <li>Dapatkan 1.5x Multiplier Poin</li>
                  <li>Free Upsize Minuman Mingguan</li>
                  <li>2x Poin di Hari Ulang Tahun</li>
                </ul>
              </div>

              <div className={`p-4 rounded-2xl border space-y-2 ${currentTier === 'PLATINUM' ? 'bg-indigo-50 border-indigo-400 ring-1 ring-indigo-300' : 'bg-indigo-50/70 border-indigo-200'}`}>
                <div className="flex items-center justify-between font-bold text-indigo-900">
                  <div className="flex items-center gap-2">
                    <span className="text-base">💎</span>
                    <span>Platinum Tier</span>
                  </div>
                  {currentTier === 'PLATINUM' && (
                    <span className="px-2 py-0.5 rounded-full bg-indigo-700 text-white text-[10px] uppercase">Tier Anda</span>
                  )}
                </div>
                <p className="text-indigo-800">
                  Syarat: Minimal {tierSettings.platinumMinTransactions} Transaksi Valid atau belanja {formatRupiah(tierSettings.platinumMinSpending)}.
                </p>
                <ul className="list-disc list-inside text-indigo-700 space-y-1 pl-1">
                  <li>Dapatkan 2x Multiplier Poin Utama</li>
                  <li>Diskon Spesial VIP &amp; Prioritas Pesanan</li>
                  <li>Undangan Eksklusif Event Tasting Kopi</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
